import React, { useRef, useEffect, useState } from 'react';
import { Input, Avatar, Upload } from 'antd';
import styled from 'styled-components';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { UserOutlined, CloseOutlined, SmileOutlined, PictureOutlined, SendOutlined } from '@ant-design/icons';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import axios from 'axios';
import { formatDateTime } from '../../utils';

const ChatWindow = styled.div<{ visible: boolean }>`
  position: fixed;
  bottom: 0;
  right: ${props => props.visible ? '320px' : '-320px'};
  width: 320px;
  height: 480px;
  background: white;
  border-radius: 8px 8px 0 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  z-index: 998;
  transition: right 0.3s ease;
`;

const ChatHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .username {
    font-weight: 500;
  }
`;

const CloseButton = styled(CloseOutlined)`
  cursor: pointer;
  padding: 4px;
  color: #999;
  transition: all 0.3s;

  &:hover {
    color: #666;
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #f7f7f7;
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MessageItem = styled.div<{ $isMine: boolean }>`
  display: flex;
  flex-direction: ${props => props.$isMine ? 'row-reverse' : 'row'};
  align-items: flex-end;
  gap: 8px;
  max-width: 85%;
  ${props => props.$isMine ? 'margin-left: auto;' : 'margin-right: auto;'}
`;

const MessageContent = styled.div<{ $isMine: boolean }>`
  background: ${props => props.$isMine ? '#0084ff' : '#e4e6eb'};
  color: ${props => props.$isMine ? '#fff' : '#000'};
  padding: 8px 12px;
  border-radius: 18px;
  font-size: 14px;
  max-width: 100%;
  word-wrap: break-word;
`;

const MessageTime = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
  text-align: center;
`;

const DateDivider = styled.div`
  text-align: center;
  margin: 16px 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    width: 100%;
    height: 1px;
    background: #e4e6eb;
    z-index: 1;
  }

  span {
    background: #f7f7f7;
    padding: 0 12px;
    color: #65676b;
    font-size: 12px;
    position: relative;
    z-index: 2;
  }
`;

const InputContainer = styled.div`
  padding: 12px;
  background: white;
  border-top: 1px solid #f0f0f0;
  position: relative;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: #f0f2f5;
  border-radius: 20px;
  padding: 8px 12px;
`;

const StyledInput = styled(Input.TextArea)`
  border: none;
  background: transparent;
  resize: none;
  padding: 0;
  
  &.ant-input {
    box-shadow: none !important;
  }

  &:focus {
    box-shadow: none !important;
  }
`;

const IconButton = styled.button<{ $active?: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${props => props.$active ? '#0073e6' : '#0084ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  transform: ${props => props.$active ? 'scale(1.1)' : 'scale(1)'};

  &:hover {
    color: #0073e6;
  }
`;

const EmojiPickerWrapper = styled.div<{ visible: boolean }>`
  position: fixed;
  bottom: 84px;
  right: ${props => props.visible ? '650px' : '640px'};
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transform-origin: bottom right;
  transition: all 0.2s ease;

  opacity: ${props => props.visible ? 1 : 0};
  transform: ${props => props.visible 
    ? 'scale(1) translate(0, 0)' 
    : 'scale(0.7) translate(50%, 20%)'
  };
  pointer-events: ${props => props.visible ? 'auto' : 'none'};

  .EmojiPickerReact {
    border: none !important;
    box-shadow: none !important;
  }
`;

const ImagePreview = styled.div`
  padding: 8px;
  border-top: 1px solid #f0f0f0;
  
  img {
    max-width: 100px;
    max-height: 100px;
    border-radius: 8px;
  }
`;

interface Message {
  id: string;
  content: string;
  isMine: boolean;
  timestamp: Date;
  type?: 'text' | 'emoji';
}

interface ChatDialogProps {
  visible: boolean;
  onClose: () => void;
  userInfo?: {
    id: number;
    name: string;
    avatar: string;
  };
  conversation?: {
    _id: string;
    userId: number;
    sellerId: number;
    userInfo: {
      id: number;
      name: string;
      avatar: string;
    };
    sellerInfo: {
      id: number;
      name: string;
      avatar: string;
    };
    createdAt: number;
    updatedAt: number;
    status: string;
  };
  messages: Message[];
  onSendMessage: (content: string, imageUrl?: string | null) => void;
}

const ChatDialog: React.FC<ChatDialogProps> = ({
  visible,
  onClose,
  userInfo,
  conversation,
  messages: propMessages,
  onSendMessage
}) => {
  const messageListRef = useRef<HTMLDivElement>(null);
  const [inputValue, setInputValue] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const inputRef = useRef<any>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const fetchMessages = async (pageNumber: number) => {
    if (!conversation?._id) return;

    try {
      setLoading(true);
      const response = await axios.get('/admin/chat/get-list-message', {
        params: {
          conversationId: conversation._id,
          skip: (pageNumber - 1) * limit,
          limit
        },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.data.code === 0) {
        const { data, total: totalItems } = response.data;
        
        const formattedMessages = data.map((msg: any) => ({
          id: msg._id,
          content: msg.content,
          isMine: msg.senderId === conversation.sellerId,
          timestamp: new Date(msg.createdAt),
          type: msg.type
        }));

        if (pageNumber === 1) {
          setMessages(formattedMessages);
        } else {
          setMessages(prev => [...formattedMessages, ...prev]);
        }
        
        setTotal(totalItems);
        setHasMore(messages.length < totalItems);
      }
    } catch (error) {
      console.error('Lỗi khi lấy tin nhắn:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (conversation?._id) {
      setPage(1);
      setMessages([]);
      fetchMessages(1);
    }
  }, [conversation?._id]);

  const handleScroll = () => {
    if (!messageListRef.current) return;
    
    const { scrollTop } = messageListRef.current;
    if (scrollTop === 0 && !loading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchMessages(nextPage);
    }
  };

  useEffect(() => {
    const messageList = messageListRef.current;
    if (messageList) {
      messageList.addEventListener('scroll', handleScroll);
      return () => messageList.removeEventListener('scroll', handleScroll);
    }
  }, [page, loading, hasMore]);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  const onEmojiClick = (emojiData: EmojiClickData) => {
    const cursor = inputRef.current?.resizableTextArea?.textArea?.selectionStart || inputValue.length;
    const text = inputValue;
    const newText = text.slice(0, cursor) + emojiData.emoji + text.slice(cursor);
    setInputValue(newText);
  };

  const handleImageUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const handleSend = () => {
    if (inputValue.trim() || imageUrl) {
      onSendMessage(inputValue.trim(), imageUrl);
      setInputValue('');
      setImageUrl(null);
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.emoji-picker-container') && !target.closest('.emoji-button')) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  console.log(userInfo, conversation, 'userInfo');

  return (
    <ChatWindow visible={visible}>
      <ChatHeader> 
        <UserInfo>
          <Avatar 
            icon={<UserOutlined />}
            src={userInfo?.avatar}
            size={32}
          />
          <span className="username">{userInfo?.name}</span>
        </UserInfo>
        <CloseButton onClick={onClose} />
      </ChatHeader>

      <ChatContainer>
        <MessageList ref={messageListRef}>
          {loading && <div>Đang tải...</div>}
          <DateDivider>
            <span>{formatDateTime(conversation?.createdAt)}</span>
          </DateDivider>
          {messages.map((message) => (
            <MessageItem key={message.id} $isMine={message.isMine}>
              {!message.isMine && (
                <Avatar 
                  icon={<UserOutlined />}
                  src={userInfo?.avatar}
                  size={28}
                />
              )}
              <div>
                <MessageContent $isMine={message.isMine}>
                  {message.content}
                </MessageContent>
                <MessageTime>
                  {formatDistanceToNow(message.timestamp, { 
                    addSuffix: true,
                    locale: vi 
                  })}
                </MessageTime>
              </div>
            </MessageItem>
          ))}
        </MessageList>
        
        <InputContainer>
          <EmojiPickerWrapper 
            visible={showEmojiPicker} 
            className="emoji-picker-container"
          >
            <EmojiPicker 
              onEmojiClick={onEmojiClick}
              width={320}
              height={400}
            />
          </EmojiPickerWrapper>

          {imageUrl && (
            <ImagePreview>
              <img src={imageUrl} alt="Preview" />
              <CloseOutlined onClick={() => setImageUrl(null)} />
            </ImagePreview>
          )}

          <InputWrapper>
            <IconButton 
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="emoji-button"
              $active={showEmojiPicker}
            >
              <SmileOutlined />
            </IconButton>

            <StyledInput
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Nhập tin nhắn..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              bordered={false}
            />

            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <IconButton>
                <PictureOutlined />
              </IconButton>
            </Upload>

            {(inputValue.trim() || imageUrl) && (
              <IconButton onClick={handleSend}>
                <SendOutlined />
              </IconButton>
            )}
          </InputWrapper>
        </InputContainer>
      </ChatContainer>
    </ChatWindow>
  );
};

export default ChatDialog; 